import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ownerService from "../../../../services/owner.service";
import {
  IOwnerStatistics,
  IOwnerStatisticsSeries,
  OwnerStatisticsRange,
} from "../../../../services/api.types";
import {
  buildChartDataset,
  isMetricEmpty,
  OWNER_STATS_RANGES,
  OwnerStatsMetric,
} from "./statisticsChart";

const METRICS: {
  key: OwnerStatsMetric;
  labelKey: string;
  titleKey: string;
  valueColor: "primary" | "success" | "error" | "warning";
}[] = [
  {
    key: "total",
    labelKey: "ownerScreens.totalWashes",
    titleKey: "ownerScreens.chartTitleTotal",
    valueColor: "primary",
  },
  {
    key: "completed",
    labelKey: "ownerScreens.completed",
    titleKey: "ownerScreens.chartTitleCompleted",
    valueColor: "success",
  },
  {
    key: "cancelled",
    labelKey: "ownerScreens.cancelled",
    titleKey: "ownerScreens.chartTitleCancelled",
    valueColor: "error",
  },
  {
    key: "missed",
    labelKey: "ownerScreens.missedRequests",
    titleKey: "ownerScreens.chartTitleMissed",
    valueColor: "warning",
  },
];

const centred = {
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  gap: 1,
} as const;

const Statistics = () => {
  const { ownerID } = useParams();
  const { t } = useTranslation();
  const theme = useTheme();
  const [stats, setStats] = useState<IOwnerStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedMetric, setSelectedMetric] = useState<OwnerStatsMetric>("total");
  const [range, setRange] = useState<OwnerStatisticsRange>("30d");
  const [series, setSeries] = useState<IOwnerStatisticsSeries | null>(null);
  const [seriesLoading, setSeriesLoading] = useState(true);
  const [seriesError, setSeriesError] = useState(false);

  useEffect(() => {
    let active = true;
    const fetchStats = async () => {
      if (!ownerID) return;
      setLoading(true);
      setError(null);
      try {
        const data = await ownerService.getOwnerStatistics(ownerID);
        if (active) setStats(data);
      } catch (err: any) {
        if (active) setError(err.message || "Failed to fetch statistics");
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchStats();
    return () => {
      active = false;
    };
  }, [ownerID]);

  // One response holds every metric's series for the range, so switching the
  // selected metric does not refetch.
  useEffect(() => {
    let active = true;
    const fetchSeries = async () => {
      if (!ownerID) return;
      setSeriesLoading(true);
      setSeriesError(false);
      try {
        const data = await ownerService.getOwnerStatisticsSeries(ownerID, range);
        if (active) setSeries(data);
      } catch {
        if (active) setSeriesError(true);
      } finally {
        if (active) setSeriesLoading(false);
      }
    };
    fetchSeries();
    return () => {
      active = false;
    };
  }, [ownerID, range]);

  const selectedMeta =
    METRICS.find((m) => m.key === selectedMetric) ?? METRICS[0];

  const { dataset, chartSeries } = useMemo(() => {
    const meta = METRICS.find((m) => m.key === selectedMetric) ?? METRICS[0];
    const rows = series ? buildChartDataset(series.series, series.granularity) : [];
    const barSeries =
      selectedMetric === "missed"
        ? [
            {
              dataKey: "missedExpired",
              label: t("ownerScreens.seriesExpired"),
              stack: "missed",
              color: theme.palette.warning.light,
            },
            {
              dataKey: "missedDeclined",
              label: t("ownerScreens.seriesDeclined"),
              stack: "missed",
              color: theme.palette.warning.dark,
            },
          ]
        : [
            {
              dataKey: selectedMetric,
              label: t(meta.labelKey),
              color: theme.palette[meta.valueColor].main,
            },
          ];
    return { dataset: rows, chartSeries: barSeries };
  }, [series, selectedMetric, theme, t]);

  const renderChartBody = () => {
    if (seriesLoading) {
      return (
        <Box sx={centred}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            {t("ownerScreens.chartLoading")}
          </Typography>
        </Box>
      );
    }
    if (seriesError) {
      return (
        <Box sx={centred}>
          <Typography color="error">{t("ownerScreens.chartError")}</Typography>
        </Box>
      );
    }
    if (!series || isMetricEmpty(series.series, selectedMetric)) {
      return (
        <Box sx={centred}>
          <Typography color="text.secondary">
            {t("ownerScreens.chartEmpty")}
          </Typography>
        </Box>
      );
    }
    return (
      <BarChart
        dataset={dataset}
        xAxis={[{ scaleType: "band", dataKey: "label" }]}
        series={chartSeries}
        height={320}
        hideLegend={selectedMetric !== "missed"}
      />
    );
  };

  return (
    <Box className="p-4">
      <Typography variant="h4" gutterBottom>
        {t("ownerScreens.statistics")}
      </Typography>

      {loading && (
        <Typography variant="body1" color="text.secondary">
          {t("ownerScreens.loadingStatistics")}
        </Typography>
      )}

      {!loading && error && (
        <Paper className="p-6">
          <Typography variant="body1" color="error">
            {error}
          </Typography>
        </Paper>
      )}

      {!loading && !error && stats && (
        <>
          <Grid container spacing={3} alignItems="stretch">
            {METRICS.map((m) => {
              const selected = selectedMetric === m.key;
              return (
                <Grid
                  key={m.key}
                  size={{ xs: 12, sm: 6, md: 3 }}
                  sx={{ display: "flex" }}
                >
                  <Card
                    raised={selected}
                    sx={{
                      width: "100%",
                      height: "100%",
                      border: 2,
                      borderColor: selected
                        ? `${m.valueColor}.main`
                        : "transparent",
                    }}
                  >
                    <CardActionArea
                      onClick={() => setSelectedMetric(m.key)}
                      aria-pressed={selected}
                      sx={{ height: "100%" }}
                    >
                      <CardContent
                        sx={{
                          height: "100%",
                          minHeight: 148,
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          textAlign: "center",
                        }}
                      >
                        <Typography
                          variant="h4"
                          color={
                            m.valueColor === "primary"
                              ? "primary"
                              : `${m.valueColor}.main`
                          }
                        >
                          {m.key === "missed" ? stats.missed : stats[m.key]}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t(m.labelKey)}
                        </Typography>
                        {m.key === "missed" && (
                          <Typography variant="caption" color="text.secondary">
                            {t("ownerScreens.missedRequestsBreakdown", {
                              expired: stats.missedExpired,
                              declined: stats.missedDeclined,
                            })}
                          </Typography>
                        )}
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          <Paper sx={{ mt: 3, p: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Typography variant="h6">{t(selectedMeta.titleKey)}</Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={range}
                onChange={(_, v) => {
                  if (v) setRange(v as OwnerStatisticsRange);
                }}
                aria-label={t("ownerScreens.chartRangeAria")}
              >
                {OWNER_STATS_RANGES.map((r) => (
                  <ToggleButton key={r} value={r}>
                    {t(`ownerScreens.range${r}`)}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
            <Typography variant="caption" color="text.secondary">
              {t("ownerScreens.chartTimezoneCaption")}
            </Typography>
            <Box sx={{ height: 320, mt: 2 }}>{renderChartBody()}</Box>
            {selectedMetric === "missed" && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mt: 1 }}
              >
                {t("ownerScreens.chartMissedCaption")}
              </Typography>
            )}
          </Paper>
        </>
      )}
    </Box>
  );
};

export default Statistics;
